import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { AE_METRICS, BDR_METRICS, VIEW_OPTIONS, viewRowKind, networkOrderIndex, } from "../utils";
import { monthGroupLabel, monthValueLabel, isCurrentMonth, isPastMonth, } from "./shared";
/* ===== Cell renderers ===== */
const NaText = () => (_jsx(Typography, { component: "span", className: "all-Font-Sizes sr-na-text", sx: { fontStyle: "italic", color: "#a4a7ae", whiteSpace: "nowrap" }, children: "N/A" }));
// Comma-grouped; whole numbers by default, rate metrics keep 2 decimals (see RATE_VALUE_METRICS).
const formatReportNumber = (value, decimals = 0) => decimals > 0
    ? value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : Math.round(value).toLocaleString("en-US");
// Conv. Rate (AE+BDR) and Happened % (BDR) show 2-decimal VALUE cells; goals (GoalChip) stay whole.
const RATE_VALUE_METRICS = new Set(["conv_rate", "happened_pct"]);
const rateValueDecimals = (metric) => RATE_VALUE_METRICS.has(metric.key) ? 2 : 0;
// Format a metric value as plain text ($ / % / number) — used in the week tooltip.
const formatMetricValue = (metric, value) => {
    const safe = value === null || value === undefined || !Number.isFinite(value) ? 0 : value;
    if (metric.format === "currency")
        return `$${formatReportNumber(safe)}`;
    if (metric.format === "percent")
        return `${formatReportNumber(safe, rateValueDecimals(metric))}%`;
    return formatReportNumber(safe);
};
// Hover content: weekly breakdown of a value cell (Friday→Thursday weeks of the month).
const WeekBreakdown = ({ metric, weeks }) => (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: "2px" }, children: weeks.map((w, i) => (_jsxs(Typography, { className: "all-Font-Sizes", sx: { color: "#fff", whiteSpace: "nowrap" }, children: [w.label, " : ", formatMetricValue(metric, w.v)] }, i))) }));
// Value with gray $/% symbols; missing renders as 0 (only GOAL cells show N/A).
const ValueText = ({ metric, value, bold, }) => {
    const safe = value === null || value === undefined || !Number.isFinite(value) ? 0 : value;
    const weight = bold ? 600 : 400;
    const sym = { color: "#a4a7ae", fontWeight: weight };
    const numStyle = { color: "#27272a", fontWeight: weight };
    if (metric.format === "currency") {
        return (_jsxs(Typography, { component: "span", className: "all-Font-Sizes", sx: { whiteSpace: "nowrap" }, children: [_jsx(Box, { component: "span", sx: sym, children: "$" }), _jsx(Box, { component: "span", sx: numStyle, children: formatReportNumber(safe) })] }));
    }
    if (metric.format === "percent") {
        return (_jsxs(Typography, { component: "span", className: "all-Font-Sizes", sx: { whiteSpace: "nowrap" }, children: [_jsx(Box, { component: "span", sx: numStyle, children: formatReportNumber(safe, rateValueDecimals(metric)) }), _jsx(Box, { component: "span", sx: sym, children: "%" })] }));
    }
    return (_jsx(Typography, { component: "span", className: "all-Font-Sizes", sx: { ...numStyle, whiteSpace: "nowrap" }, children: formatReportNumber(safe) }));
};
// GOAL % vs goal — green/red variance pill with trend arrow; faded N/A when no goal.
const GoalChip = ({ pct, bold }) => {
    if (pct === null || pct === undefined || !Number.isFinite(pct))
        return _jsx(NaText, {});
    const isPos = pct >= 0;
    return (_jsxs(Box, { component: "span", sx: {
            backgroundColor: isPos ? "#ecfdf3" : "#fef3f2",
            borderRadius: "16px",
            px: 1,
            py: 0.5,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
        }, children: [_jsxs(Typography, { component: "span", className: "all-Font-Sizes", sx: { fontWeight: bold ? 700 : 500, color: isPos ? "#027a48" : "#b42318" }, children: [formatReportNumber(Math.abs(pct)), "%"] }), _jsx("img", { src: isPos ? "/assets/trend-up.svg" : "/assets/trending-down.svg", alt: "" })] }));
};
// "Last 7 days" tooltip label: [today-7, today-1] inclusive, US Eastern (mirrors the backend).
const last7RangeTooltip = () => {
    const easternToday = new Date(new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(new Date()));
    const end = new Date(easternToday);
    end.setDate(end.getDate() - 1); // yesterday
    const start = new Date(easternToday);
    start.setDate(start.getDate() - 7); // 7 days back from today
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fmt(start)} to ${fmt(end)}`;
};
// Only these BDR measures are editable/drillable; Conv. Rate and Happened % are derived (not clickable).
const EDITABLE_BDR_METRICS = ["outreach", "booked", "happened", "enrollments"];
/* Hook: columns + rows + renderCell for the report matrix */
export const useSalesReportingTable = (tab, view, year, months, viewData, showYtd, onEditCell, // BDR only: opens the Legacy Data Edit popup for a past-month cell
onViewDetails // BDR user rows: opens the current-month details popup
) => {
    const metrics = tab === "AE" ? AE_METRICS : BDR_METRICS;
    const viewLabel = useMemo(() => VIEW_OPTIONS.find((v) => v.value === view)?.label || "Top Line Reporting", [view]);
    // View name inserted into row labels for team breakdown views (not Top Line), e.g. "Sidd New To Network Outreach".
    const rowViewName = useMemo(() => {
        if (view === "top_line" || viewRowKind(view) !== "team")
            return "";
        return viewLabel.replace(/^By\s+/, "").replace(/\s+Calls$/, "");
    }, [view, viewLabel]);
    const showGoals = !!viewData?.hasGoals;
    const columns = useMemo(() => {
        const cols = [
            // BDR always heads the row column with "ALL BDR TEAM"; AE shows the view name.
            { id: "row_label", label: tab === "BDR" ? "All BDR Team" : viewLabel, width: "210px", align: "left" },
        ];
        for (const m of months) {
            const current = isCurrentMonth(year, m);
            // Views without goals (tier1–4) show a single flat value column per month.
            if (!showGoals) {
                cols.push({ id: `m${m}_val`, label: monthValueLabel(m, year), width: "90px", align: "left" });
                continue;
            }
            const children = [
                { id: `m${m}_val`, label: monthValueLabel(m, year), width: "90px", align: "left" },
                { id: `m${m}_goal`, label: current ? "MTD Goal" : "Goal", width: "90px", align: "left" },
            ];
            // LAST 7 DAYS only shows for the Top Line Reporting view (current month).
            if (current && view === "top_line") {
                children.push({ id: `m${m}_last7`, label: "Last 7 Days", width: "100px", align: "left", tooltip: last7RangeTooltip() });
            }
            cols.push({
                id: `m${m}`,
                label: monthGroupLabel(m, year),
                align: "center",
                children,
            });
        }
        // YTD only in the multi-month ("All time") view — hidden when a single month is selected.
        if (showYtd) {
            if (showGoals) {
                cols.push({
                    id: "ytd",
                    label: "YTD",
                    align: "center",
                    children: [{ id: "ytd_total", label: "Total", width: "90px", align: "left" }],
                });
            }
            else {
                cols.push({ id: "ytd_total", label: "Total", width: "90px", align: "left" });
            }
        }
        return cols;
    }, [tab, viewLabel, months, year, view, showGoals, showYtd]);
    // Rows: for each metric, one row per entity (from the backend) followed by a Total row.
    const tableRows = useMemo(() => {
        const out = [];
        let entityRows = viewData?.rows || [];
        // "By Affiliate Network" rows show ONLY the allow-listed networks, ordered to match the old
        // portal — AE and BDR use different orders. Unlisted networks are dropped; others keep backend order.
        if (viewRowKind(view) === "network" && entityRows.length) {
            entityRows = [...entityRows]
                .filter((e) => networkOrderIndex(e.name, tab) !== Number.MAX_SAFE_INTEGER)
                .sort((a, b) => networkOrderIndex(a.name, tab) - networkOrderIndex(b.name, tab));
        }
        for (const metric of metrics) {
            entityRows.forEach((entity, idx) => {
                out.push({
                    id: `${metric.key}-${entity.key}-${idx}`,
                    label: rowViewName
                        ? `${entity.name} ${rowViewName} ${metric.label}`
                        : `${entity.name} ${metric.label}`,
                    metric,
                    data: entity.metrics,
                    is_total: false,
                    entityKey: entity.key,
                    entityName: entity.name,
                });
            });
            if (entityRows.length) {
                out.push({
                    id: `${metric.key}-total`,
                    label: rowViewName ? `Total ${rowViewName} ${metric.label}` : `Total ${metric.label}`,
                    metric,
                    data: viewData?.total.metrics || null,
                    is_total: true,
                    entityKey: "",
                    entityName: "",
                });
            }
        }
        return out;
    }, [metrics, viewData, rowViewName, view, tab]);
    const renderCell = (row, column) => {
        if (column.id === "row_label") {
            return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: row.is_total ? 600 : 400, color: "#18181b", whiteSpace: "nowrap" }, children: row.label }));
        }
        const cells = row.data?.[row.metric.key];
        if (column.id === "ytd_total") {
            return _jsx(ValueText, { metric: row.metric, value: cells?.ytd ?? null, bold: row.is_total });
        }
        const match = /^m(\d{1,2})_(val|goal|last7)$/.exec(String(column.id));
        if (!match)
            return null;
        const month = match[1];
        const part = match[2];
        if (part === "val") {
            const cell = cells?.m[month];
            const valueNode = _jsx(ValueText, { metric: row.metric, value: cell?.v ?? null, bold: row.is_total });
            // Weekly breakdown on hover is an AE-only feature; BDR cells stay plain.
            if (tab === "AE" && cell?.weeks && cell.weeks.length) {
                return (_jsx(HoverTooltip, { title: _jsx(WeekBreakdown, { metric: row.metric, weeks: cell.weeks }), placement: "top", children: _jsx(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                            minHeight: 24,
                            cursor: "default",
                        }, children: valueNode }) }));
            }
            const monthNum = Number(month);
            const isNetwork = viewRowKind(view) === "network";
            if (tab === "BDR" && !row.is_total && row.entityKey && EDITABLE_BDR_METRICS.includes(row.metric.key)) {
                const ctx = {
                    entityKey: row.entityKey,
                    entityName: row.entityName,
                    isNetwork,
                    view,
                    month: monthNum,
                    year,
                };
                const clickable = (handler, title) => (_jsx(Box, { onClick: handler, title: title, sx: {
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        minHeight: 24,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                    }, children: valueNode }));
                if (!!onEditCell && !isNetwork && isPastMonth(year, monthNum)) {
                    return clickable(() => onEditCell(ctx), "Edit Legacy Data");
                }
                if (!!onViewDetails && !isNetwork && isCurrentMonth(year, monthNum)) {
                    return clickable(() => onViewDetails(ctx), "View details");
                }
            }
            return valueNode;
        }
        if (part === "last7") {
            return _jsx(ValueText, { metric: row.metric, value: cells?.last7 ?? null, bold: row.is_total });
        }
        // GOAL / MTD GOAL — precomputed % (already prorated server-side for current month).
        return _jsx(GoalChip, { pct: cells?.m[month]?.g ?? null, bold: row.is_total });
    };
    return { columns, tableRows, renderCell };
};
